import { Box, Typography } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { doc, setDoc } from 'firebase/firestore';
import * as Sentry from '@sentry/react';
import { db } from '@/firebase/firebase';
import Loading from '@/components/Loading';
import { RouteMap } from '@/routes/Routes';
import dripService from '@/services/dripService';
import { isFirebaseOfflineError } from '@/services/errorReportingService';
import { useAuthStore } from '@/stores/authStore';
import { useBannerStore } from '@/stores/bannerStore';
import AnimatedBackground from './components/AnimatedBackground';
import CompletionCelebration from './components/CompletionCelebration';
import OnboardingStep from './OnboardingStep';
import ProgressIndicator from './components/ProgressIndicator';
import { useOnboardingStatus } from './useOnboardingStatus';
import { useOnboardingSteps } from './useOnboardingSteps';

const OnboardingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useAuthStore();
  const { headerHeight } = useBannerStore();
  const { steps, loading: stepsLoading, isMobile } = useOnboardingSteps();
  // Use userData.id (Firestore doc ID / backend ID) — NOT currentUser.uid (Firebase Auth UID)
  // Firestore security rules require using the Firestore document ID for read/write access
  // Using currentUser.uid causes "Missing or insufficient permissions" errors
  const {
    status: onboardingStatus,
    loading: statusLoading,
    error: statusError,
    setOnboardingStatus,
  } = useOnboardingStatus(userData?.id);
  const [currentStepIndex, setCurrentStepIndexState] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');
  const redirectTimeoutRef = useRef<number | null>(null);
  const redirectStartedRef = useRef(false);
  const initialHashNavigationDoneRef = useRef(false);
  const lastUpdatedAnchorRef = useRef<string | null>(null);
  const lastDripAnchorRef = useRef<string | null>(null);
  const pageMinHeight = `calc(100vh - ${headerHeight}px)`;

  // Check for URL hash anchor
  const hash = location.hash.replace('#', '');
  const isCompletedAnchor = hash === 'completed';

  const isLoading = stepsLoading;

  // Helper to update URL hash without triggering navigation
  const updateUrlHash = (anchorId: string | undefined) => {
    if (anchorId) {
      window.history.replaceState(null, '', `#${anchorId}`);
    }
  };

  // Wrapper to set step index and update URL hash
  const setCurrentStepIndex = (index: number) => {
    setCurrentStepIndexState(index);
    const step = steps[index];
    if (step?.anchorId) {
      updateUrlHash(step.anchorId);
    }
  };
  const totalSteps = steps.length;

  // Update onboardingStatus to 'completed' when #completed anchor is present
  useEffect(() => {
    if (!isCompletedAnchor || statusLoading || !userData?.id || statusError) {
      return;
    }

    if (onboardingStatus !== 'completed') {
      void setOnboardingStatus('completed');
    }
  }, [isCompletedAnchor, statusLoading, statusError, onboardingStatus, userData?.id, setOnboardingStatus]);

  // Update onboardingStatus to current step's anchor_id when step changes
  // Default to 'step_1' if no anchorId is available
  useEffect(() => {
    // Skip if completed anchor, loading, no user data, or error
    if (isCompletedAnchor || statusLoading || !userData?.id || statusError) {
      return;
    }

    // Skip if steps haven't loaded yet
    if (steps.length === 0) {
      return;
    }

    const currentStep = steps[currentStepIndex];
    // Default to 'step_1' if step has no anchorId
    const anchorId = currentStep?.anchorId || 'step_1';

    // Only update if anchorId changed from last update (prevents loops)
    if (anchorId !== lastUpdatedAnchorRef.current) {
      lastUpdatedAnchorRef.current = anchorId;
      void setOnboardingStatus(anchorId);
    }
  }, [currentStepIndex, steps, isCompletedAnchor, statusLoading, statusError, userData?.id, setOnboardingStatus]);

  // Send onboarding step to Drip when step changes
  useEffect(() => {
    // Skip if no user email
    if (!userData?.email) {
      return;
    }

    // For completed, we don't need steps to be loaded
    if (isCompletedAnchor) {
      if (lastDripAnchorRef.current !== 'completed') {
        lastDripAnchorRef.current = 'completed';
        dripService.updatePluginContext(userData.email, {
          onboardingStatus: 'completed',
        });
      }
      return;
    }

    // For regular steps, wait for steps to load
    if (steps.length === 0) {
      return;
    }

    const currentStep = steps[currentStepIndex];
    const anchorId = currentStep?.anchorId || 'step_1';

    // Only send if anchorId changed from last Drip update (prevents duplicate calls)
    if (anchorId !== lastDripAnchorRef.current) {
      lastDripAnchorRef.current = anchorId;
      dripService.updatePluginContext(userData.email, {
        onboardingStatus: anchorId,
      });
    }
  }, [currentStepIndex, steps, isCompletedAnchor, userData?.email]);

  // Persist isMobile flag on the user's Firebase document once when onboarding starts on a mobile device
  const mobilePersistedRef = useRef(false);
  useEffect(() => {
    if (!isMobile || !userData?.id || mobilePersistedRef.current) {
      return;
    }
    const docRef = doc(db, 'users', userData.id);
    setDoc(docRef, { onboardingIsMobile: true }, { merge: true })
      .then(() => {
        mobilePersistedRef.current = true;
      })
      .catch((error: unknown) => {
        if (!isFirebaseOfflineError(error)) {
          Sentry.captureException(error, {
            tags: { feature: 'onboarding' },
            extra: { context: 'persisting isMobile flag', userId: userData.id },
          });
        }
      });
  }, [isMobile, userData?.id]);

  /**
   * Get the redirect URL after onboarding completes.
   * Checks for stored redirect URL (e.g., from shared note link) first,
   * then falls back to HOME.
   */
  const getPostOnboardingRedirect = (): string => {
    const storedRedirect = sessionStorage.getItem('postOnboardingRedirect');
    if (storedRedirect) {
      sessionStorage.removeItem('postOnboardingRedirect');
      return storedRedirect;
    }
    return RouteMap.HOME;
  };

  // Show celebration and start redirect when #completed anchor is present
  useEffect(() => {
    if (isCompletedAnchor && !redirectStartedRef.current) {
      redirectStartedRef.current = true;
      setShowCelebration(true);
      redirectTimeoutRef.current = window.setTimeout(() => {
        navigate(getPostOnboardingRedirect(), { replace: true });
      }, 4000);
    }

    // Only clear timeout on unmount, not on every re-render
    return () => {
      if (redirectTimeoutRef.current) {
        window.clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, [isCompletedAnchor, navigate]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (totalSteps === 0) {
      setCurrentStepIndexState(0);
      return;
    }

    if (currentStepIndex > totalSteps - 1) {
      setCurrentStepIndexState(totalSteps - 1);
    }
  }, [isLoading, totalSteps, currentStepIndex]);

  // Handle URL hash navigation to jump to a specific step on initial load (runs ONCE)
  // Also sets default hash to step_1 when landing on /onboarding without a hash
  useEffect(() => {
    // Skip if already done initial navigation
    if (initialHashNavigationDoneRef.current) {
      return;
    }

    if (isLoading || totalSteps === 0) {
      return;
    }

    // Skip if completed anchor
    if (hash === 'completed') {
      return;
    }

    // If no hash, set default to first step's anchorId (or 'step_1')
    if (!hash) {
      initialHashNavigationDoneRef.current = true;
      const firstStep = steps[0];
      const defaultAnchorId = firstStep?.anchorId || 'step_1';
      updateUrlHash(defaultAnchorId);
      setCurrentStepIndexState(0);
      return;
    }

    // Hash exists, find matching step
    const targetStepIndex = steps.findIndex((s) => s.anchorId === hash);
    if (targetStepIndex !== -1) {
      initialHashNavigationDoneRef.current = true;
      setCurrentStepIndexState(targetStepIndex);
    }
  }, [isLoading, totalSteps, steps, hash]);

  const activeStep = useMemo(() => steps[currentStepIndex], [steps, currentStepIndex]);
  const isLastStep = currentStepIndex >= totalSteps - 1;

  const handleComplete = () => {
    setShowCelebration(true);
    updateUrlHash('completed');
    redirectTimeoutRef.current = window.setTimeout(() => {
      const dest = getPostOnboardingRedirect();
      navigate(dest, { replace: true });
    }, 4000);
  };

  const handleNext = () => {
    if (!activeStep) {
      return;
    }

    if (activeStep.redirectUrl) {
      navigate(activeStep.redirectUrl, { replace: true });
      return;
    }

    if (isLastStep) {
      handleComplete();
      return;
    }

    setDirection('forward');
    const nextIndex = currentStepIndex + 1;
    setCurrentStepIndex(nextIndex);
  };

  const handleBack = () => {
    if (currentStepIndex <= 0) {
      return;
    }

    setDirection('backward');
    setCurrentStepIndex(currentStepIndex - 1);
  };

  const handleCtaAction = (action: string) => {
    const trimmedAction = action?.trim();
    if (!trimmedAction) {
      return;
    }

    const lowerAction = trimmedAction.toLowerCase();
    const dangerousSchemes = ['javascript:', 'data:', 'vbscript:', 'file:'];

    if (dangerousSchemes.some((scheme) => lowerAction.startsWith(scheme))) {
      return;
    }

    if (lowerAction.startsWith('http://') || lowerAction.startsWith('https://')) {
      window.open(trimmedAction, '_blank', 'noopener,noreferrer');
      return;
    }

    const target = trimmedAction.startsWith('/') ? trimmedAction : `/${trimmedAction}`;
    navigate(target);
  };

  // Show celebration screen FIRST when #completed anchor is present
  // This must come BEFORE the loading check - we don't need steps data to show celebration
  if (isCompletedAnchor) {
    return (
      <Box
        sx={{
          minHeight: pageMinHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          position: 'relative',
        }}
      >
        <AnimatedBackground />
        <CompletionCelebration />
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: pageMinHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Loading />
      </Box>
    );
  }

  if (!activeStep && totalSteps === 0) {
    return (
      <Box
        sx={{
          minHeight: pageMinHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <AnimatedBackground />
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Box
            sx={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              borderRadius: '23px',
              p: { xs: 3, sm: 4 },
              maxWidth: 560,
              width: '100%',
              boxShadow: '0px 20px 60px rgba(0, 0, 0, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                mb: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              You are all set!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Welcome to NoteGo. Let's get started.
            </Typography>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleComplete}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: '100px',
                padding: '12px 32px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
              }}
            >
              Continue to app
            </motion.button>
          </Box>
        </motion.div>
      </Box>
    );
  }

  if (!activeStep) {
    return (
      <Box
        sx={{
          minHeight: pageMinHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Loading />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: pageMinHeight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        flexDirection: 'column',
        px: { xs: 2, sm: 3, md: 4 },
        pt: { xs: 3, sm: 4 },
        pb: { xs: 4, sm: 6 },
        gap: 4,
        position: 'relative',
        overflowX: 'hidden',
        overflowY: 'auto',
      }}
    >
      <AnimatedBackground />

      {/* Progress Indicator */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <ProgressIndicator currentStep={currentStepIndex} totalSteps={totalSteps} />
      </motion.div>

      {/* Step Content with Smooth Transitions */}
      <Box sx={{ width: '100%', maxWidth: 860, position: 'relative' }}>
        <AnimatePresence mode="wait" custom={direction}>
          <OnboardingStep
            key={currentStepIndex}
            step={activeStep}
            stepIndex={currentStepIndex}
            totalSteps={totalSteps}
            isLastStep={isLastStep}
            onBack={handleBack}
            onNext={handleNext}
            onCtaAction={handleCtaAction}
            direction={direction}
          />
        </AnimatePresence>
      </Box>

      {/* Celebration Animation */}
      {showCelebration && <CompletionCelebration />}
    </Box>
  );
};

export default OnboardingPage;
